import { type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { LibraryItemStatus } from "@/lib/library";
import type { LibraryCategory } from "@/lib/libraryCategories";
import type { LibraryRecord } from "@/components/library/myLibraryHelpers";

type Args = {
  categories: LibraryCategory[];
  filteredItemIds: string[];
  allVisibleSelected: boolean;
  selectedItemIds: Set<string>;
  setItems: Dispatch<SetStateAction<LibraryRecord[]>>;
  setSelectedItemIds: Dispatch<SetStateAction<Set<string>>>;
  setSelectMode: Dispatch<SetStateAction<boolean>>;
  setBulkBusy: Dispatch<SetStateAction<boolean>>;
};

export const useMyLibraryBulkActions = ({
  categories,
  filteredItemIds,
  allVisibleSelected,
  selectedItemIds,
  setItems,
  setSelectedItemIds,
  setSelectMode,
  setBulkBusy,
}: Args) => {
  const toggleItemSelection = (itemId: string) => {
    setSelectedItemIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectMode(false);
    setSelectedItemIds(new Set());
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedItemIds((current) => new Set(Array.from(current).filter((id) => !filteredItemIds.includes(id))));
      return;
    }
    setSelectedItemIds((current) => new Set([...Array.from(current), ...filteredItemIds]));
  };

  const applyCategoriesToSelection = (categoryIds: string[]) => {
    const selectedCategories = categories
      .filter((category) => categoryIds.includes(category.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    setItems((current) =>
      current.map((item) => {
        if (!selectedItemIds.has(item.id)) return item;
        const merged = new Map(item.categories.map((category) => [category.id, category]));
        selectedCategories.forEach((category) => merged.set(category.id, category));
        return {
          ...item,
          categories: Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name)),
        };
      }),
    );
  };

  const removeCategoriesFromSelectionState = (categoryIds: string[]) => {
    const categoryIdSet = new Set(categoryIds);
    setItems((current) =>
      current.map((item) =>
        selectedItemIds.has(item.id)
          ? { ...item, categories: item.categories.filter((category) => !categoryIdSet.has(category.id)) }
          : item,
      ),
    );
  };

  const handleBulkAssignCategories = async (categoryIds: string[]) => {
    if (!selectedItemIds.size || !categoryIds.length) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedItemIds);
      const payload = ids.flatMap((libraryItemId) =>
        categoryIds.map((libraryCategoryId) => ({
          library_item_id: libraryItemId,
          library_category_id: libraryCategoryId,
        })),
      );
      const { error } = await supabase
        .from("library_item_categories")
        .upsert(payload, { onConflict: "library_item_id,library_category_id", ignoreDuplicates: true });
      if (error) throw error;
      applyCategoriesToSelection(categoryIds);
      toast.success(`Updated ${ids.length} source${ids.length === 1 ? "" : "s"}`);
      exitSelectionMode();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkRemoveCategories = async (categoryIds: string[]) => {
    if (!selectedItemIds.size || !categoryIds.length) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedItemIds);
      const { error } = await supabase
        .from("library_item_categories")
        .delete()
        .in("library_item_id", ids)
        .in("library_category_id", categoryIds);
      if (error) throw error;
      removeCategoriesFromSelectionState(categoryIds);
      toast.success(`Removed categories from ${ids.length} source${ids.length === 1 ? "" : "s"}`);
      exitSelectionMode();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedItemIds.size) return;
    const ids = Array.from(selectedItemIds);
    if (!confirm(`Remove ${ids.length} source${ids.length === 1 ? "" : "s"} from My Library?`)) return;
    setBulkBusy(true);
    try {
      const { error } = await supabase.from("library_items").delete().in("id", ids);
      if (error) throw error;
      setItems((current) => current.filter((item) => !selectedItemIds.has(item.id)));
      toast.success(`Removed ${ids.length} source${ids.length === 1 ? "" : "s"}`);
      exitSelectionMode();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkSetStatus = async (status: LibraryItemStatus) => {
    if (!selectedItemIds.size) return;
    const ids = Array.from(selectedItemIds);
    setBulkBusy(true);
    try {
      const { error } = await supabase.from("library_items").update({ status }).in("id", ids);
      if (error) throw error;
      setItems((current) => current.map((item) => (selectedItemIds.has(item.id) ? { ...item, status } : item)));
      toast.success(`Updated status on ${ids.length} source${ids.length === 1 ? "" : "s"}`);
      exitSelectionMode();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBulkBusy(false);
    }
  };

  return {
    toggleItemSelection,
    exitSelectionMode,
    toggleSelectAllVisible,
    handleBulkAssignCategories,
    handleBulkRemoveCategories,
    handleBulkDelete,
    handleBulkSetStatus,
  };
};
